import { createFileRoute } from '@tanstack/react-router'
import { CenteredSpinner } from '@portal/pkg-ui'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { continueCognitoLogout } from '@auth/data/cognitoLogout'
import { ResumePageView } from '@auth/resume/view/ResumePageView'

/**
 * The logout couldn't be prepared, or the departure for Resume timed out. Trying again reloads the
 * continuation, which prepares the logout again: going to Resume instead would find a session the
 * failed logout left live, and return to it.
 */
function ContinueLogoutError() {
  const { t } = useTranslation('auth')
  const resume = t('resumePage.resume')
  const title = t('resumePage.tryAgain', { resume })

  return (
    <ResumePageView
      logoAlt={t('resumePage.logoAlt')}
      title={title}
      lines={[]}
      buttonLabel={resume}
      onButtonClick={() => window.location.reload()}
      announcement={title}
    />
  )
}

export const Route = createFileRoute('/_login/continue-logout')({
  validateSearch: z.object({
    reason: z.literal('idle').optional().catch(undefined),
    expectedSessionInstanceId: z.string().min(32).max(64).optional().catch(undefined),
  }),
  beforeLoad: ({ search }) => continueCognitoLogout(search),
  // Splits out only the error page. The spinner shows while beforeLoad waits, normally until the
  // page unloads, and after it. Code splitting can't share it between a split `component` and an
  // unsplit `pendingComponent`: the build fails, or the import leaves this module.
  codeSplitGroupings: [['errorComponent']],
  pendingComponent: CenteredSpinner,
  pendingMs: 0,
  component: CenteredSpinner,
  errorComponent: ContinueLogoutError,
})
